import { GetInputPropsReturnType } from '@mantine/form/lib/types';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import equal from 'fast-deep-equal';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useUploadImage, updateRobotConfigs, translateToAllLanguages } from '@mygpt/app';
import { I_UserFormValues, useWidgetFormContext } from '@/components/Widget/Embed/form-context';
import { useSubmitFormAsync } from '@/hooks/useSubmitFormAsync';
import { isBase64 } from '@/utils/isBase64';

/**
 * widget_configs 中需要保存前自动翻译为多语言 dict 的字段路径（相对 widget_configs 根）。
 * 新增字段只需添加路径即可。字符串值会被翻译为 {lang: text} dict；已是 dict 的值保持不变。
 */
const WIDGET_I18N_FIELD_PATHS = ['chatbot.chatGuideText.value'];

const getByPath = (obj: any, path: string): any =>
  path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

const setByPath = (obj: any, path: string, val: any) => {
  const keys = path.split('.');
  let cur = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    cur = cur[keys[i]];
    if (cur == null) return;
  }
  cur[keys[keys.length - 1]] = val;
};

const pickSourceText = (val: any, sourceLang?: string): string => {
  if (typeof val === 'string') return val.trim();
  if (val && typeof val === 'object') {
    const fromLang = sourceLang ? val[sourceLang] : '';
    return String(fromLang || Object.values(val).find((v) => v && String(v).trim()) || '').trim();
  }
  return '';
};

const translateI18nFields = async (widgetConfigs: any, previousConfigs: any, sourceLang?: string) => {
  await Promise.all(
    WIDGET_I18N_FIELD_PATHS.map(async (path) => {
      const val = getByPath(widgetConfigs, path);
      // 优化：与上次保存快照相等则跳过，避免无谓的 LLM 调用
      const prevVal = getByPath(previousConfigs, path);
      if (equal(val, prevVal)) return;

      const sourceText = pickSourceText(val, sourceLang);
      if (!sourceText) return;
      try {
        const { translations } = await translateToAllLanguages(sourceText, sourceLang);
        setByPath(widgetConfigs, path, translations);
      } catch (e) {
        console.warn(`[i18n] translate ${path} failed, keep original value`, e);
      }
    })
  );
};

export const useWidgetForm = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { uploadImage } = useUploadImage();

  const { form, setWidgetConfigs, widgetConfigs } = useWidgetFormContext();
  const [loading, { close: closeLoading, open: openLoading }] = useDisclosure(false);

  // 和上次更新相比，没有任何配置项发生变化
  const dataHasNotChange = () => {
    const hasNotChanged = equal(form.values, widgetConfigs);
    if (hasNotChanged) {
      return true;
    }

    return false;
  };

  const submit = async () => {
    if (!id) return;

    const { values } = form;

    // 如果和上次更新相比，没有任何配置项变化，那么直接退出
    if (dataHasNotChange()) {
      return;
    }

    openLoading();

    const { chatbot, chatbotAvatar, chatbotIcon } = values;

    // 过滤空白
    const welcomeMessageValue = chatbot.welcomeMessage.value
      .split(/[\n]/)
      .filter((item) => !!item.trim())
      .join('\n');

    // 处理多语言welcome message
    const multiLanguageValues = {
      zh: chatbot.welcomeMessage.multiLanguageValues.zh
        .split(/[\n]/)
        .filter((item) => !!item.trim())
        .join('\n'),
      en: chatbot.welcomeMessage.multiLanguageValues.en
        .split(/[\n]/)
        .filter((item) => !!item.trim())
        .join('\n'),
      ja: chatbot.welcomeMessage.multiLanguageValues.ja
        .split(/[\n]/)
        .filter((item) => !!item.trim())
        .join('\n'),
      ko: chatbot.welcomeMessage.multiLanguageValues.ko
        .split(/[\n]/)
        .filter((item) => !!item.trim())
        .join('\n'),
    };

    const suggestedMessagesValue = chatbot.suggestedMessages.value
      .split(/[\n]/)
      .filter((item) => !!item.trim())
      .join('\n');

    const chatbotIconCustomUrl = chatbotIcon.icon.url ?? '';
    const chatbotAvatarCustomUrl = chatbotAvatar.url ?? '';
    const chatHeaderImageUrl = chatbot.chatHeaderImage.url ?? '';

    // 判断是否旧的自定义图片
    let chatbotIconUrl = !isBase64(chatbotIconCustomUrl) ? chatbotIconCustomUrl : '',
      chatbotAvatarUrl = !isBase64(chatbotAvatarCustomUrl) ? chatbotAvatarCustomUrl : '',
      backgroundImageUrl = !isBase64(chatHeaderImageUrl) ? chatHeaderImageUrl : '';

    if (chatbotIcon.icon.type === 'custom' && chatbotIcon.icon.file) {
      const res = await uploadImage({ file: chatbotIcon.icon.file, fileType: 'avatar', aiId: id });
      chatbotIconUrl = res.data.url;
    }

    if (chatbotAvatar.type === 'custom' && chatbotAvatar.file) {
      const res = await uploadImage({ file: chatbotAvatar.file, fileType: 'avatar', aiId: id });
      chatbotAvatarUrl = res.data.url;
    }

    // 处理背景图片上传
    if (chatbot.chatHeaderImage.checked && isBase64(chatHeaderImageUrl)) {
      // 将base64转换为File对象进行上传
      const dataURItoBlob = (dataURI: string) => {
        const byteString = atob(dataURI.split(',')[1]);
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ab], { type: mimeString });
      };

      const blob = dataURItoBlob(chatHeaderImageUrl);
      const ext = blob.type.split('/')[1] || 'jpg';
      const file = new File([blob], `background.${ext}`, { type: blob.type });

      const res = await uploadImage({ file, fileType: 'avatar', aiId: id });
      backgroundImageUrl = res.data.url;
    }

    const widget_configs: I_UserFormValues = {
      ...values,
      chatbot: {
        ...values.chatbot,
        welcomeMessage: {
          checked: values.chatbot.welcomeMessage.checked,
          value: welcomeMessageValue,
          type: values.chatbot.welcomeMessage.type,
          multiLanguageValues: multiLanguageValues,
        },
        suggestedMessages: {
          checked: values.chatbot.suggestedMessages.checked,
          value: suggestedMessagesValue,
          type: values.chatbot.suggestedMessages.type,
          node_list: values.chatbot.suggestedMessages.node_list,
          greetingMessage: values.chatbot.suggestedMessages.greetingMessage,
        },
        signatureMessages: {
          ...values.chatbot.signatureMessages,
          displayType: 'all', // 兼容处理
        },
        chatHeaderImage: {
          checked: values.chatbot.chatHeaderImage.checked,
          url: backgroundImageUrl,
        },
      },
      chatbotIcon: {
        ...values.chatbotIcon,
        icon: {
          ...values.chatbotIcon.icon,
          file: undefined,
          url: chatbotIconUrl,
        },
      },
      chatbotAvatar: {
        ...values.chatbotAvatar,
        file: undefined,
        url: chatbotAvatarUrl,
      },
      digitalHuman: {
        ...values.digitalHuman,
        // 保护textureUrl字段，如果存在则保持不变
        ...(values.digitalHuman?.textureUrl && { textureUrl: values.digitalHuman.textureUrl }),
      },
    };

    // 保存前：对白名单字段做多语言翻译；与上次保存快照相同时跳过
    await translateI18nFields(widget_configs, widgetConfigs, i18next.language);

    // 把翻译后的 dict 同步回 form 值，避免"前端只有单语言、服务端已全量"的状态不一致
    WIDGET_I18N_FIELD_PATHS.forEach((path) => {
      const translated = getByPath(widget_configs, path);
      if (translated !== undefined) form.setFieldValue(path, translated);
    });

    updateRobotConfigs({ aiId: id, configs: { widget_configs: JSON.stringify(widget_configs) } })
      .then(() => {
        showNotification({
          message: t('save_success'),
          color: 'green.5',
        });

        setWidgetConfigs(widget_configs);
      })
      .finally(() => {
        closeLoading();
      });
  };

  const submitInNextTick = useSubmitFormAsync(submit);

  /**
   * 覆盖表单的 onChange/onBlur 事件行为
   * 实现在用户操作表单项后的合适时机自动触发保存行为
   * @param prop 对应表单的prop
   * @param eventName 对应表单组件的事件，比如 onBlur,onChange
   * @returns
   */
  const onOverrideChangeForForm = (prop: string, eventName: keyof GetInputPropsReturnType = 'onChange') => {
    return (data?: any) => {
      // 如果检测到是输入框的 onBlur 事件，并且该行为是用户触发（因为发现引入的Polyfill也会触发一次 blur），那么触发保存行为
      if (data && data.type === 'blur') {
        if (data.isTrusted) {
          submitInNextTick();
        }

        // 如果是代码触发的 onBlur，返回，什么都不做。
        return;
      } else {
        // 如果是 onChange 事件被触发，说明用户操作了表单的 Switch/Checkbox/Select 等组件
        // 那么更新表单的值，然后触发保存行为

        form.getInputProps(prop)[eventName](data);

        // 如果值是数组，目前的表单项里，说明是日期范围选择器的值
        // 那么只有当两个日期值都被选择时，才触发保存行为
        // 否则不触发。
        //
        // P.S. 未来如果用这种办法无法区分出日期范围选择器，那么可以使用当前函数传入的 prop 值来区分。
        if (Array.isArray(data) && (!data[0] || !data[1])) {
          return;
        }
        submitInNextTick();
      }
    };
  };

  return { submit, loading, onOverrideChangeForForm, dataHasNotChange };
};
